import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { API, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ProductCard({ product, index = 0 }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [wished, setWished] = useState(false);
  const img = product.images?.[0] || "";
  const off = product.mrp > product.selling_price ? Math.round((1 - product.selling_price / product.mrp) * 100) : 0;
  const oos = product.outlet_in_stock === false;

  const toggleWish = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    try {
      if (wished) { await API.delete(`/wishlist/${product.id}`); setWished(false); }
      else { await API.post(`/wishlist/${product.id}`); setWished(true); toast.success("Added to wishlist"); }
    } catch { toast.error("Could not update wishlist"); }
  };

  return (
    <Link to={`/product/${product.slug || product.id}`} data-testid={`product-card-${product.sku || index}`}
      className="group bg-white border border-[#EADBD8] rounded-2xl overflow-hidden shadow-soft hover:shadow-lift hover:-translate-y-0.5 transition-all duration-300">
      <div className="relative aspect-[4/5] overflow-hidden bg-[#F9ECE8]">
        {img && <img src={img} alt={product.title} loading="lazy"
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${oos ? "grayscale opacity-60" : ""}`} />}
        <button data-testid={`wishlist-btn-${product.sku || index}`} onClick={toggleWish}
          className={`absolute top-3 right-3 p-2 rounded-full bg-white/90 shadow-sm ${wished ? "text-[#6A1B29]" : "text-stone-500"} hover:text-[#6A1B29]`}>
          <Heart className={`w-4 h-4 ${wished ? "fill-[#6A1B29]" : ""}`} />
        </button>
        {oos ? (
          <span className="absolute top-3 left-3 bg-stone-900 text-white text-[10px] font-semibold px-2 py-1 tracking-wide uppercase rounded-full" data-testid={`oos-badge-${product.sku || index}`}>Out of Stock</span>
        ) : product.stock_status !== "In Stock" && (
          <span className="absolute top-3 left-3 bg-stone-900 text-white text-[10px] font-semibold px-2 py-1 tracking-wide uppercase rounded-full">{product.stock_status}</span>
        )}
        {off > 0 && <span className="absolute bottom-3 left-3 bg-[#6A1B29] text-white text-[10px] font-bold px-2.5 py-1 rounded-full">{off}% OFF</span>}
      </div>
      <div className="p-3 sm:p-4">
        <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#6A1B29]">{product.category}</p>
        <h3 className="font-display text-base sm:text-lg font-medium text-[#231518] mt-1 line-clamp-1">{product.title}</h3>
        <div className="flex items-baseline gap-2 mt-1.5">
          <span className="text-sm sm:text-base font-semibold text-[#231518]">{inr(product.selling_price)}</span>
          {product.mrp > product.selling_price && <span className="text-xs text-stone-400 line-through">{inr(product.mrp)}</span>}
        </div>
        {product.has_variants && (product.variants || []).filter((v) => (v.status || "active") === "active").length > 0 && (
          <div className="flex items-center gap-1.5 mt-2" data-testid={`card-shades-${product.sku || index}`}>
            <div className="flex -space-x-1">
              {(product.variants || []).filter((v) => (v.status || "active") === "active").slice(0, 5).map((v, i) => (
                <span key={i} className="w-3.5 h-3.5 rounded-full border border-white ring-1 ring-stone-200" style={{ background: v.color_code || "#ccc" }} />
              ))}
            </div>
            <span className="text-[11px] text-stone-500">{(product.variants || []).filter((v) => (v.status || "active") === "active").length} shades</span>
          </div>
        )}
        <span className="inline-block mt-2 text-xs font-medium text-stone-500 group-hover:text-[#6A1B29] transition-colors">View Details →</span>
      </div>
    </Link>
  );
}
